import type { DocumentFlowDoc, FlowBlock, FlowDate, FlowSignature } from './schema';

/** A block that reserves a box at render time instead of drawing content. */
export type FlowAnchor = FlowSignature | FlowDate;

const isAnchor = (block: FlowBlock): block is FlowAnchor =>
	block.type === 'signature' || block.type === 'date';

const collectInBlocks = (blocks: FlowBlock[], into: FlowAnchor[]): void => {
	for (const block of blocks) {
		if (isAnchor(block)) {
			into.push(block);
			continue;
		}
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			for (const item of block.content) {
				collectInBlocks(item.content, into);
			}
			continue;
		}
		if (block.type === 'table') {
			for (const row of block.content) {
				for (const cell of row.content) {
					collectInBlocks(cell.content, into);
				}
			}
		}
	}
};

/** Every anchor of either kind, in document order, however deeply nested. */
export const flowAnchors = (doc: DocumentFlowDoc): FlowAnchor[] => {
	const found: FlowAnchor[] = [];
	collectInBlocks(doc.content, found);
	return found;
};

/**
 * Anchors in a table header row, which the save refuses: the row repeats on every continuation
 * page, reserving the box's height on each while only one is drawn.
 */
export const headerRowFlowAnchors = (doc: DocumentFlowDoc): FlowAnchor[] => {
	const found: FlowAnchor[] = [];
	const walk = (blocks: FlowBlock[]): void => {
		for (const block of blocks) {
			if (block.type === 'bulletList' || block.type === 'orderedList') {
				for (const item of block.content) {
					walk(item.content);
				}
				continue;
			}
			if (block.type !== 'table') {
				continue;
			}
			for (const row of block.content) {
				for (const cell of row.content) {
					if (row.attrs?.isHeader === true) {
						collectInBlocks(cell.content, found);
						continue;
					}
					// A non-header row can still hold a nested table with a header row.
					walk(cell.content);
				}
			}
		}
	};
	walk(doc.content);
	return found;
};

export const signatureAttrsOf = (anchors: FlowAnchor[]): FlowSignature['attrs'][] => {
	const found: FlowSignature['attrs'][] = [];
	for (const anchor of anchors) {
		if (anchor.type === 'signature') {
			found.push(anchor.attrs);
		}
	}
	return found;
};

export const dateAttrsOf = (anchors: FlowAnchor[]): FlowDate['attrs'][] => {
	const found: FlowDate['attrs'][] = [];
	for (const anchor of anchors) {
		if (anchor.type === 'date') {
			found.push(anchor.attrs);
		}
	}
	return found;
};

/** Anchors whose key the document uses more than once, in first-seen order. */
export const duplicatesBy = <TItem, TKey>(
	items: TItem[],
	keyOf: (item: TItem) => TKey
): TItem[] => {
	const seen = new Set<TKey>();
	const duplicated = new Map<TKey, TItem>();
	for (const item of items) {
		const key = keyOf(item);
		if (seen.has(key)) {
			duplicated.set(key, item);
			continue;
		}
		seen.add(key);
	}
	return Array.from(duplicated.values());
};
